import React, { useState } from 'react';
import { 
  Card, 
  List, 
  Typography, 
  Tag, 
  Descriptions, 
  Empty, 
  Button, 
  Modal, 
  Row, 
  Col,
  Space,
  Divider,
  Image,
  Alert
} from 'antd';
import {
  ScanOutlined,
  EyeOutlined,
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  FileImageOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const PatientPortalImagingResults = ({ data, loading }) => {
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  // Helper function to get study type icon and color
  const getStudyTypeInfo = (studyType) => {
    const types = {
      'X-Ray': { icon: <ScanOutlined />, color: '#1890ff' },
      'Chest XR': { icon: <ScanOutlined />, color: '#1890ff' },
      'CXR': { icon: <ScanOutlined />, color: '#1890ff' },
      'CT': { icon: <ScanOutlined />, color: '#722ed1' },
      'MRI': { icon: <ScanOutlined />, color: '#eb2f96' },
      'Ultrasound': { icon: <ScanOutlined />, color: '#52c41a' },
      'Heart US': { icon: <MedicineBoxOutlined />, color: '#13c2c2' },
      'Bladder US': { icon: <ScanOutlined />, color: '#52c41a' },
      'EKG': { icon: <MedicineBoxOutlined />, color: '#fa541c' },
      'Echocardiogram': { icon: <MedicineBoxOutlined />, color: '#13c2c2' },
      'Echo': { icon: <MedicineBoxOutlined />, color: '#13c2c2' },
      'Mammography': { icon: <ScanOutlined />, color: '#fa8c16' }
    };
    return types[studyType] || { icon: <FileImageOutlined />, color: '#8c8c8c' };
  };

  // Helper function to get patient-friendly study explanations
  const getStudyExplanation = (studyType) => {
    const explanations = {
      'X-Ray': 'Uses small amounts of radiation to create pictures of bones and organs.',
      'Chest XR': 'X-ray examination of your chest to check your lungs, heart, and chest bones.',
      'CXR': 'X-ray examination of your chest to check your lungs, heart, and chest bones.',
      'CT': 'Combines X-ray images taken from different angles to create detailed cross-sectional views.',
      'MRI': 'Uses magnetic fields and radio waves to create detailed images of organs and tissues.',
      'Ultrasound': 'Uses sound waves to create images of organs and structures inside the body.',
      'Heart US': 'Uses sound waves to create moving pictures of your heart and check how well it works.',
      'Bladder US': 'Uses sound waves to examine your bladder and check for any problems.',
      'EKG': 'Records the electrical activity of your heart to check for heart rhythm problems.',
      'Echocardiogram': 'Uses sound waves to create moving pictures of your heart.',
      'Echo': 'Uses sound waves to create moving pictures of your heart.',
      'Mammography': 'Special X-ray examination of the breasts to screen for breast cancer.'
    };
    return explanations[studyType] || 'Medical imaging study to examine internal body structures.';
  };

  // Helper function to determine urgency level
  const getUrgencyLevel = (findings, impression) => {
    const urgentKeywords = ['urgent', 'immediate', 'critical', 'emergency', 'acute', 'severe'];
    const followUpKeywords = ['follow-up', 'recommend', 'suggest', 'consider', 'monitor'];
    
    const text = `${findings} ${impression}`.toLowerCase();
    
    if (urgentKeywords.some(keyword => text.includes(keyword))) {
      return { level: 'urgent', color: 'red', text: 'Requires Attention' };
    } else if (followUpKeywords.some(keyword => text.includes(keyword))) {
      return { level: 'followup', color: 'orange', text: 'Follow-up Recommended' };
    } else {
      return { level: 'routine', color: 'green', text: 'Routine' };
    }
  };

  // Show detailed view of an imaging result
  const showDetailModal = (result) => {
    setSelectedResult(result);
    setDetailModalVisible(true);
  };

  // Show image viewer
  const showImageViewer = (result) => {
    // In a real implementation, this would load actual DICOM images
    setSelectedImages(result.images || []);
    setImageViewerVisible(true);
  };

  if (data.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            No imaging results found
            <br />
            <Text type="secondary">Your imaging studies will appear here once available</Text>
          </span>
        }
      />
    );
  }

  return (
    <div>
      <List
        dataSource={data}
        renderItem={(result) => {
          const studyInfo = getStudyTypeInfo(result.orderDetails?.studyType);
          const urgency = getUrgencyLevel(
            result.results?.findings || '', 
            result.results?.impression || ''
          );
          
          return (
            <List.Item style={{ padding: 0, marginBottom: 16 }}>
              <Card className="imaging-result-card" style={{ width: '100%' }}>
                <div className="imaging-result-header">
                  <div className="imaging-study-info">
                    <Title level={5} className="imaging-study-title">
                      {React.cloneElement(studyInfo.icon, { 
                        style: { marginRight: 8, color: studyInfo.color } 
                      })}
                      {result.orderDetails?.studyDescription || 'Imaging Study'}
                    </Title>
                    <div className="imaging-study-details">
                      <Space size="middle">
                        <span>
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {moment(result.results?.resultDate || result.studyDate).format('MMMM DD, YYYY')}
                        </span>
                        <span>
                          <UserOutlined style={{ marginRight: 4 }} />
                          {result.orderingProvider?.name || 'N/A'}
                        </span>
                        <Tag color={urgency.color}>{urgency.text}</Tag>
                      </Space>
                    </div>
                  </div>
                  <div className="imaging-actions">
                    <Space>
                      <Button 
                        icon={<EyeOutlined />} 
                        onClick={() => showDetailModal(result)}
                        size="small"
                      >
                        View Report
                      </Button>
                      {result.images && result.images.length > 0 && (
                        <Button 
                          icon={<FileImageOutlined />} 
                          onClick={() => showImageViewer(result)}
                          size="small"
                          type="primary"
                        >
                          View Images
                        </Button>
                      )}
                      <Button 
                        icon={<DownloadOutlined />} 
                        size="small"
                        type="text"
                      >
                        Export
                      </Button>
                    </Space>
                  </div>
                </div>

                {/* Study explanation */}
                <div style={{ marginBottom: 16, padding: 12, background: '#f6f8fa', borderRadius: 6 }}>
                  <Text style={{ fontSize: 13, color: '#666' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {getStudyExplanation(result.orderDetails?.studyType)}
                  </Text>
                </div>

                {/* Key findings summary */}
                {result.results?.findings && (
                  <div className="imaging-findings">
                    <div className="imaging-findings-title">
                      <Text strong>Key Findings:</Text>
                    </div>
                    <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                      {result.results.findings}
                    </Paragraph>
                  </div>
                )}

                {/* Impression/Conclusion */}
                {result.results?.impression && (
                  <div className="imaging-impression">
                    <div className="imaging-impression-title">
                      <Text strong>Radiologist's Impression:</Text>
                    </div>
                    <Paragraph ellipsis={{ rows: 2, expandable: true }}>
                      {result.results.impression}
                    </Paragraph>
                  </div>
                )}

                {/* Study details */}
                <Divider style={{ margin: '16px 0' }} />
                <Row gutter={16}>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Study Type: {result.orderDetails?.studyType || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Body Part: {result.orderDetails?.bodyPart || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={8} style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Order Date: {moment(result.orderDate).format('MM/DD/YYYY')}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </List.Item>
          );
        }}
      />

      {/* Detail Modal */}
      <Modal
        title={
          <span>
            <ScanOutlined style={{ marginRight: 8 }} />
            {selectedResult?.orderDetails?.studyDescription} - Full Report
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          <Button key="export" icon={<DownloadOutlined />}>
            Export Report
          </Button>
        ]}
        width={800}
      >
        {selectedResult && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Study Date">
                {moment(selectedResult.results?.resultDate || selectedResult.studyDate).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Radiologist">
                {selectedResult.radiologist?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Study Type">
                {selectedResult.orderDetails?.studyType || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Body Part">
                {selectedResult.orderDetails?.bodyPart || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Ordering Provider">
                {selectedResult.orderingProvider?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="green">Final Report</Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Clinical History */}
            {selectedResult.clinicalHistory && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Clinical History</Title>
                <Paragraph>{selectedResult.clinicalHistory}</Paragraph>
              </div>
            )}

            {/* Technique */}
            {selectedResult.technique && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Technique</Title>
                <Paragraph>{selectedResult.technique}</Paragraph>
              </div>
            )}

            {/* Findings */}
            {selectedResult.results?.findings && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Findings</Title>
                <Paragraph>{selectedResult.results.findings}</Paragraph>
              </div>
            )}

            {/* Impression */}
            {selectedResult.results?.impression && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Impression</Title>
                <Paragraph>{selectedResult.results.impression}</Paragraph>
              </div>
            )}

            {/* Recommendations */}
            {selectedResult.recommendations && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Recommendations</Title>
                <Alert
                  message="Follow-up Instructions"
                  description={selectedResult.recommendations}
                  type="info"
                  showIcon
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Image Viewer Modal */}
      <Modal
        title="Medical Images"
        open={imageViewerVisible}
        onCancel={() => setImageViewerVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImageViewerVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
      >
        <div style={{ textAlign: 'center' }}>
          {selectedImages.length > 0 ? (
            <Image.PreviewGroup>
              {selectedImages.map((image, index) => (
                <Image
                  key={index}
                  width={200}
                  src={image.url}
                  alt={`Medical Image ${index + 1}`}
                  style={{ margin: 8 }}
                />
              ))}
            </Image.PreviewGroup>
          ) : (
            <div style={{ padding: 40 }}>
              <FileImageOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Image viewing functionality will be integrated with DICOM viewer
                </Text>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PatientPortalImagingResults;
